import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  from,
  gql,
  useMutation,
  useQuery,
} from '@apollo/client'
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename'
import { offsetLimitPagination } from '@apollo/client/utilities'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect as connectRedux, useSelector } from 'react-redux'
import { compose } from 'recompose'
import { map as mapPromise } from 'bluebird'
import _castArray from 'lodash/castArray'
import _compact from 'lodash/compact'
import _defaultTo from 'lodash/defaultTo'
import _difference from 'lodash/difference'
import _filter from 'lodash/filter'
import _find from 'lodash/find'
import _get from 'lodash/get'
import _has from 'lodash/has'
import _includes from 'lodash/includes'
import _intersectionWith from 'lodash/intersectionWith'
import _isArray from 'lodash/isArray'
import _isEmpty from 'lodash/isEmpty'
import _isEqual from 'lodash/isEqual'
import _isUndefined from 'lodash/isUndefined'
import _join from 'lodash/join'
import _map from 'lodash/map'
import _matches from 'lodash/matches'
import _omit from 'lodash/omit'
import _omitBy from 'lodash/omitBy'
import _parseInt from 'lodash/parseInt'
import _pick from 'lodash/pick'
import _reduce from 'lodash/reduce'
import _set from 'lodash/set'
import _some from 'lodash/some'
import _sortBy from 'lodash/sortBy'
import _split from 'lodash/split'
import { CONTENT_STORE_KEY, locationToOptions } from 'services/content'
import { BUNDLE_LABELS } from 'services/content/bundles'
import {
  CONTENT_TYPE_SERIES,
  CONTENT_TYPE_VIDEO,
  getAllContentTypes,
} from 'services/content/content-types'
import {
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER,
  CONTENT_REFERENCE_TYPE_MEDITATION_DURATION,
  CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS,
  CONTENT_REFERENCE_TYPE_MEDITATION_STYLE,
  CONTENT_REFERENCE_TYPE_YOGA_TEACHER,
  CONTENT_REFERENCE_TYPE_YOGA_DURATION,
  CONTENT_REFERENCE_TYPE_YOGA_FOCUS,
  CONTENT_REFERENCE_TYPE_YOGA_STYLE,
  CONTENT_REFERENCE_TYPE_YOGA_LEVEL,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_PRODUCT,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_SERIES,
  CONTENT_REFERENCE_TYPES,
  CONTENT_REFERENCE_TYPE_GENRES,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
} from 'services/content-references/types'
import { getContentReferenceTypesContentDictionary } from 'services/content-references'
import { getViewError } from 'services/data'
import { LANGUAGE_EN, LANGUAGE_FR, LANGUAGE_ES, LANGUAGE_DE } from 'services/languages/constants'
import { createAuthorization } from 'services/auth'
import { selectConfig } from 'services/config/redux/selectors'
import { selectAuth } from 'services/auth/redux/selectors'
import { getLocationQuery } from 'services/location'
import Container from 'muicss/lib/react/container'
import {
  DataGridPro,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridFilterInputMultipleSingleSelect,
  GridRowEditStartReasons,
  GridRowModes,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  useGridApiContext,
} from '@mui/x-data-grid-pro'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import CancelIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import SaveIcon from '@mui/icons-material/Save'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import Tooltip from '@mui/material/Tooltip'
import {
  transformOperator,
  filterOperatorsDate,
  filterOperatorsNumeric,
  filterOperatorsSingleSelect,
  filterOperatorsString,
} from '@/services/mui-grid-pro'
import { ContentProviderProviders } from '@/__generated__/gql/graphql'
import { createPrioritySortedColumns, createNewColumnOrder } from './helpers'
import './index.scss'

/**
 * The order of the array affects behavior:
 * English First: The column shows English. Clicking edit shows English. Typing shows English results
 * French First: The column shows French. Clicking edit shows English. Typing shows French results
 * Because of https://gaiatv.atlassian.net/browse/EL-1065, we want English first
 */
const DEFAULT_LANGUAGE_SELECTION = [LANGUAGE_EN]
const LANGUAGE_SELECTION_OPTIONS = [
  { label: 'English', value: [LANGUAGE_EN] },
  { label: 'Spanish', value: [LANGUAGE_ES] },
  { label: 'German', value: [LANGUAGE_DE] },
  { label: 'French', value: [LANGUAGE_FR] },
  { label: 'English/Spanish', value: [LANGUAGE_EN, LANGUAGE_ES] },
  { label: 'English/German', value: [LANGUAGE_EN, LANGUAGE_DE] },
  { label: 'English/French', value: [LANGUAGE_EN, LANGUAGE_FR] },
]
const SNACKBAR_DEFAULT_DURATION = 6000
const SORT_DEFAULT = [{ field: 'publishedAtDate', sort: 'desc' }]
const COLUMN_DEFAULT_VISIBILITY = {
  bundle: false,
  cast: false,
  [CONTENT_REFERENCE_TYPE_CATEGORY]: false,
  copyright: false,
  createdAt: false,
  description: false,
  displayType: false,
  featureMediaId: false,
  filterType: false,
  language: false,
  id: false,
  instructor: false,
  contentSearchNoIndex: false,
  excludeFromRecommendations: false,
  [CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST]: false,
  [CONTENT_REFERENCE_TYPE_INTERVIEW_HOST]: false,
  [CONTENT_REFERENCE_TYPE_MARKETING_CENTERS]: false,
  [CONTENT_REFERENCE_TYPE_MEDITATION_DURATION]: false,
  [CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS]: false,
  [CONTENT_REFERENCE_TYPE_MEDITATION_STYLE]: false,
  [CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER]: false,
  contentAssetReferences: false,
  previewMediaId: false,
  primaryLanguage: false,
  productType: false,
  studio: false,
  titleGroupId: false,
  titleId: false,
  unpublishedAt: false,
  updatedAt: false,
  weight: false,
  [CONTENT_REFERENCE_TYPE_YOGA_DURATION]: false,
  [CONTENT_REFERENCE_TYPE_YOGA_FOCUS]: false,
  [CONTENT_REFERENCE_TYPE_YOGA_LEVEL]: false,
  [CONTENT_REFERENCE_TYPE_YOGA_STYLE]: false,
  [CONTENT_REFERENCE_TYPE_YOGA_TEACHER]: false,
}
const COLUMN_DEFAULT_ORDER = {
  // visible - defined order
  0: 'actions',
  1: 'contentId',
  2: 'contentType',
  3: 'title',
  4: CONTENT_REFERENCE_TYPE_SERIES,
  5: 'published',
  6: 'publishedAtDate',
  7: 'contentProviderProvider',
  8: 'path',
  9: 'seasonNumber',
  10: 'episodeNumber',
  11: CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  12: CONTENT_REFERENCE_TYPE_CENTERS,
  13: CONTENT_REFERENCE_TYPE_PRODUCT,
  14: 'contentPolicyPolicyId',
  15: CONTENT_REFERENCE_TYPE_GENRES,
  16: CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
  17: CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
  // hidden - alphabetical order
  18: 'bundle',
  19: 'cast',
  20: CONTENT_REFERENCE_TYPE_CATEGORY,
  21: 'copyright',
  22: 'createdAt',
  23: 'description',
  24: 'displayType',
  25: 'featureMediaId',
  26: 'filterType',
  27: 'id',
  28: 'excludeFromRecommendations',
  29: 'contentSearchNoIndex',
  30: 'instructor',
  31: CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  32: CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  33: 'language',
  34: CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  35: CONTENT_REFERENCE_TYPE_MEDITATION_DURATION,
  36: CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS,
  37: CONTENT_REFERENCE_TYPE_MEDITATION_STYLE,
  38: CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER,
  39: 'contentAssetReferences',
  40: 'previewMediaId',
  41: 'primaryLanguage',
  42: 'productType',
  43: 'titleGroupId',
  44: 'titleId',
  45: 'studio',
  46: 'updatedAt',
  47: 'unpublishedAt',
  48: 'weight',
  49: CONTENT_REFERENCE_TYPE_YOGA_DURATION,
  50: CONTENT_REFERENCE_TYPE_YOGA_FOCUS,
  51: CONTENT_REFERENCE_TYPE_YOGA_LEVEL,
  52: CONTENT_REFERENCE_TYPE_YOGA_STYLE,
  53: CONTENT_REFERENCE_TYPE_YOGA_TEACHER,
}
const COLUMN_DEFAULT_PINS = {
  left: ['actions'],
  right: [],
}

const CONTENT_PROVIDER_OPTIONS = [
  { value: '', label: 'Any' },
  { value: ContentProviderProviders.YogaInternational, label: 'Yoga International' },
  { value: ContentProviderProviders.GaiaBonus, label: 'Gaia Bonus' },
  { value: ContentProviderProviders.GaiaWordpress, label: 'Gaia Wordpress' },
  { value: ContentProviderProviders.Shopify, label: 'Shopify' },
]

const CONTENT_LIST_QUERY = gql`
  query ContentList(
    $language: String = "en"
    $offset: Int = 0
    $limit: Int = 25
    $order: [[String]]
    $where: ContentWhereInput = {}
    $references: [ContentContentReferencesToFilterInput] = []
    $referencesEvery: [ContentContentReferencesToFilterInput] = []
    $contentProviders: [ContentContentProviderFilterInput] = []
    $contentProvidersNot: [ContentContentProviderFilterInput] = []
  ) {
    contentList(
      offset: $offset
      limit: $limit
      order: $order
      where: $where
      references: $references
      referencesEvery: $referencesEvery
      contentProviders: $contentProviders
      contentProvidersNot: $contentProvidersNot
    ) {
      count
      limit
      offset
      content {
        id
        titleGroupId
        titleId
        contentId
        contentType
        language
        title
        path
        published
        publishedAtDate
        unpublishedAt
        contentReferencesFrom {
          id
          contentTo(language: [$language, "_primary"]) {
            language
            titleGroupId
            title
          }
          contentReferenceType {
            id
            type
          }
        }
        contentAssetReferences {
          asset {
            mimeType
            originalFileName
          }
        }
        episodeNumber
        seasonNumber
        contentPolicyPolicyId
        primaryLanguage
        contentSearchNoIndex
        excludeFromRecommendations
        filterType
        bundle
        displayType
        productType
        weight
        familyFriendly
        seoTitle
        seoDescription
        seoCanonicalUrl
        seoKeywords
        seoRobotsNoFollow
        seoRobotsNoIndex
        previewMediaId
        featureMediaId
        dayNumber
        productionStatus
        instructor
        director
        producer
        writer
        cast
        copyright
        studio
        description
        descriptionFormat
        shortDescriptionFormat
        directionsLabel
        directionsDescription
        ctaLabel
        ctaPath
        ctaDeeplinkPath
        assetsLabel
        assetsDescription
        peopleListId
        createdAt
        updatedAt
        contentProvider {
          provider
          providerKey
        }
      }
    }
  }
`
const CONTENT_UPDATE_MUTATION = gql`
  mutation ContentUpdate($content: ContentInput!, $language: String!, $generatePath: Boolean) {
    contentUpdate(content: $content, generatePath: $generatePath) {
      content {
        id
        titleGroupId
        titleId
        contentId
        contentType
        language
        title
        path
        published
        publishedAtDate
        unpublishedAt
        contentReferencesFrom {
          id
          contentTo(language: [$language, "_primary"]) {
            language
            titleGroupId
            title
          }
          contentReferenceType {
            id
            type
          }
        }
        episodeNumber
        seasonNumber
        contentPolicyPolicyId
        primaryLanguage
        contentSearchNoIndex
        excludeFromRecommendations
        filterType
        bundle
        displayType
        productType
        weight
        familyFriendly
        seoTitle
        seoDescription
        seoCanonicalUrl
        seoKeywords
        seoRobotsNoFollow
        seoRobotsNoIndex
        previewMediaId
        featureMediaId
        dayNumber
        productionStatus
        instructor
        director
        producer
        writer
        cast
        copyright
        studio
        descriptionFormat
        shortDescriptionFormat
        directionsLabel
        directionsDescription
        ctaLabel
        ctaPath
        ctaDeeplinkPath
        assetsLabel
        assetsDescription
        peopleListId
        createdAt
        updatedAt
      }
    }
  }
`
const CONTENT_REFERENCE_TYPE_OPTIONS_QUERY = gql`
  query ContentReferenceTypeOptionList(
    $offset: Int = 0
    $limit: Int = 500
    $order: [[String]] = [["title", "asc"]]
    $where: ContentWhereInput = {}
    $language: [String] = ["en", "_primary"]
  ) {
    contentList(offset: $offset, limit: $limit, order: $order, where: $where, language: $language) {
      content {
        language
        titleGroupId
        title
      }
    }
  }
`
const CONTENT_REFERENCE_CREATE_MUTATION = gql`
  mutation ContentReferenceCreate($contentReference: ContentReferenceCreateInput) {
    contentReferenceCreate(contentReference: $contentReference) {
      contentReference {
        id
        fromContentContentId
        fromContentContentType
        toContentContentId
        toContentContentType
        contentReferenceTypesId
      }
    }
  }
`
const CONTENT_REFERENCE_DELETE_MUTATION = gql`
  mutation ContentReferenceDelete($contentReference: ContentReferenceDeleteInput) {
    contentReferenceDelete(contentReference: $contentReference) {
      contentReference {
        id
        fromContentContentId
        fromContentContentType
        toContentContentId
        toContentContentType
        contentReferenceTypesId
      }
    }
  }
`

function paginationModelToOffsetLimit (paginationModel) {
  const { page, pageSize } = paginationModel
  return {
    offset: page * pageSize,
    limit: pageSize,
  }
}
function modelsToOrder (filterModel, sortModel) {
  const references = _filter(
    filterModel.items,
    (i) => CONTENT_REFERENCE_TYPES.includes(i.field) && !_isEmpty(i.value),
  )
  return {
    order: _some(references, _matches({ field: 'series' }))
      ? [
          ['seasonNumber', 'asc'],
          ['episodeNumber', 'asc'],
        ]
      : _map(sortModel, ({ field, sort }) => [field, sort]),
  }
}
function filterModelToWhere (filterModel, languageSelection) {
  const contentReferencesFilterKey =
    filterModel?.logicOperator === 'or' ? 'references' : 'referencesEvery'
  const items = _filter(
    filterModel.items,
    (i) => !CONTENT_REFERENCE_TYPES.includes(i.field) && !_isEmpty(i.value),
  )
  const references = _filter(
    filterModel.items,
    (i) => CONTENT_REFERENCE_TYPES.includes(i.field) && !_isEmpty(i.value),
  )

  // Extract contentProviderProvider filter if present
  const providerFilter = _find(items, { field: 'contentProviderProvider' })
  let contentProviders = []
  let contentProvidersNot = []
  let filteredItems = items
  if (providerFilter) {
    const { operator, value } = providerFilter
    const isNonEmpty = Array.isArray(value)
      ? value.length > 0 && value.some((v) => v !== '')
      : value !== '' && value !== undefined && value !== null
    if (isNonEmpty) {
      if (operator === 'equals') {
        contentProviders = Array.isArray(value)
          ? value.filter((v) => v !== '').map((v) => ({ provider: v }))
          : [{ provider: value }]
      } else if (operator === 'notEquals') {
        contentProvidersNot = Array.isArray(value)
          ? value.filter((v) => v !== '').map((v) => ({ provider: v }))
          : [{ provider: value }]
      }
    }
    // Remove from items so it's not included in where
    filteredItems = _filter(items, (i) => i.field !== 'contentProviderProvider')
  }

  const buildWhereQueryItem = (item) => {
    const { field, operator, value } = item
    const graphQLOperator = transformOperator(operator)
    switch (field) {
      case 'contentId':
        return {
          [graphQLOperator]: _isArray(value) ? _map(value, _parseInt) : _parseInt(value),
        }
      case 'contentPolicyPolicyId':
        return {
          [graphQLOperator]: _isArray(value) ? _map(value, _parseInt) : _parseInt(value),
        }
      case 'publishedAtDate':
        return {
          [graphQLOperator]: new Date(value).toISOString(),
        }
      case 'published':
        return {
          [graphQLOperator]: value === 'true',
        }
      default:
        return {
          [graphQLOperator]: _includes(['like', 'ilike'], graphQLOperator) ? `%${value}%` : value,
        }
    }
  }
  return {
    where: !_isEmpty(filteredItems)
      ? _reduce(
          filteredItems,
          (acc, i) => {
            acc[i.field] = {
              ...acc[i.field],
              ...buildWhereQueryItem(i),
            }
            return acc
          },
          { language: { in: languageSelection } },
        )
      : { language: { in: languageSelection } },
    [contentReferencesFilterKey]: !_isEmpty(references)
      ? _reduce(
          references,
          (acc, r) => {
            const { field, value } = r
            return [
              ...acc,
              ..._map(_castArray(value), (v) => {
                const [contentId, contentType] = _split(v, '-')
                return {
                  contentId: _parseInt(contentId),
                  contentType,
                  type: field,
                }
              }),
            ]
          },
          [],
        )
      : [],
    ...(contentProviders.length > 0 ? { contentProviders } : {}),
    ...(contentProvidersNot.length > 0 ? { contentProvidersNot } : {}),
  }
}

function CustomToolbar (props) {
  const { filterModel, setFilterModel, languageSelection, setLanguageSelection } = props
  const contentIdSearchValue =
    _find(filterModel.items, { field: 'contentId', operator: '=' })?.value || ''
  const titleSearchValue =
    _find(filterModel.items, { field: 'title', operator: 'contains' })?.value || ''
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <Box sx={{ flexGrow: 1 }} />
      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
        <TextField
          id="title-search"
          label="Title"
          size="small"
          type="search"
          value={titleSearchValue}
          onChange={(e) =>
            setFilterModel({
              items: [
                // Enable this once the datagrid pro version has be enabled
                // ..._filter(filterModel.items, ({ field }) => field !== 'title'),
                {
                  field: 'title',
                  operator: 'contains',
                  value: e.target.value,
                },
              ],
            })
          }
        />
      </FormControl>
      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
        <TextField
          id="content-id-search"
          label="Content ID"
          size="small"
          type="search"
          value={contentIdSearchValue}
          onChange={(e) =>
            setFilterModel({
              items: [
                // Enable this once the datagrid pro version has be enabled
                // ..._filter(filterModel.items, ({ field }) => field !== 'contentId'),
                {
                  field: 'contentId',
                  operator: '=',
                  value: e.target.value,
                },
              ],
            })
          }
        />
      </FormControl>
      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
        <InputLabel id="language-select-label">Language</InputLabel>
        <Select
          labelId="language-select-label"
          id="language-select"
          value={languageSelection.toString()}
          label="Language"
          onChange={(e) => {
            const newLanguageSelection = e.target.value.split(',')
            setLanguageSelection(newLanguageSelection)
          }}
        >
          {_map(LANGUAGE_SELECTION_OPTIONS, ({ label, value }) => (
            <MenuItem key={value} value={value.toString()}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </GridToolbarContainer>
  )
}

function buildQueryVariables (type, languageSelection) {
  const contentReferenceTypeData = getContentReferenceTypesContentDictionary().get(type)
  const contentType = { eq: contentReferenceTypeData.getIn(['contentType', 0]) }
  let bundle
  let filterType
  let fallbackToPrimaryLanguage = false
  switch (type) {
    case CONTENT_REFERENCE_TYPE_SERIES:
    case CONTENT_REFERENCE_TYPE_PRODUCT:
      break
    default:
      fallbackToPrimaryLanguage = true
      bundle = contentReferenceTypeData.has('bundle')
        ? { eq: contentReferenceTypeData.get('bundle') }
        : undefined
      filterType = contentReferenceTypeData.has('filterType')
        ? { like: contentReferenceTypeData.get('filterType') }
        : undefined
      break
  }
  return {
    order: [['title', 'asc']],
    where: _omitBy(
      {
        contentType,
        bundle,
        filterType,
        language: !fallbackToPrimaryLanguage ? { in: languageSelection } : undefined,
      },
      _isUndefined,
    ),
    language: fallbackToPrimaryLanguage ? [...languageSelection, '_primary'] : undefined,
  }
}
function ContentReferenceSelect (props) {
  const { id, value: valueProp, field, multiple = false, optionsData = [] } = props
  const apiRef = useGridApiContext()
  const handleChange = async (event, item) => {
    const isValid = await apiRef.current.setEditCellValue({ id, field, value: item })
    if (isValid) {
      apiRef.current.stopCellEditMode({ id, field })
    }
  }

  return (
    <Autocomplete
      fullWidth
      value={valueProp}
      multiple={multiple}
      options={optionsData}
      getOptionLabel={({ title = '' }) => title}
      filterSelectedOptions
      onChange={handleChange}
      getOptionKey={({ titleGroupId }) => titleGroupId}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={_isEmpty(valueProp) ? 'Choose an option' : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>{params.InputProps.endAdornment}</>,
          }}
        />
      )}
      // Discussion around behavior and warnings for '' not being an option can be found here:
      // https://stackoverflow.com/questions/61947941/material-ui-autocomplete-warning-the-value-provided-to-autocomplete-is-invalid
      // `freeSolo` and `forcePopupIcon` set based on the above discussion
      isOptionEqualToValue={(o, v) =>
        !_isEmpty(v) && _has(v, 'titleGroupId')
          ? o.titleGroupId === v.titleGroupId
          : o.titleGroupId === v
      }
      freeSolo
      forcePopupIcon={true}
    />
  )
}

function ContentListRows (props) {
  const [languageSelection, setLanguageSelection] = useState(DEFAULT_LANGUAGE_SELECTION)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [sortModel, setSortModel] = useState(SORT_DEFAULT)
  const [filterModel, setFilterModel] = useState({ items: [] })
  const [rowModesModel, setRowModesModel] = useState({})
  const [contentUpdate] = useMutation(CONTENT_UPDATE_MUTATION)
  const [contentReferenceCreate] = useMutation(CONTENT_REFERENCE_CREATE_MUTATION)
  const [contentReferenceDelete] = useMutation(CONTENT_REFERENCE_DELETE_MUTATION)
  const [snackbar, setSnackbar] = useState(null)
  const [rowCountState, setRowCountState] = useState(data?.contentList?.count || 0)
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(COLUMN_DEFAULT_VISIBILITY)
  const [columnOrder, setColumnOrder] = useState(COLUMN_DEFAULT_ORDER)
  const [pinnedColumns, setPinnedColumns] = useState(COLUMN_DEFAULT_PINS)
  const [locationPathname, setLocationPathname] = useState('')

  const { loading, data } = useQuery(CONTENT_LIST_QUERY, {
    variables: {
      ...paginationModelToOffsetLimit(paginationModel),
      ...filterModelToWhere(filterModel, languageSelection),
      ...modelsToOrder(filterModel, sortModel),
    },
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      displaySnackbar(error.message, 'error', true)
    },
  })

  const { data: seriesOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_SERIES, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: rainbowCategoryOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })
  const { data: productOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_PRODUCT, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })
  const { data: genreOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_GENRES, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })
  const { data: thirdPartyGenreOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })
  const { data: contentOriginOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: centersOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_CENTERS, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: categoryOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_CATEGORY, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: marketingCentersOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_MARKETING_CENTERS, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: meditationFocusOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: meditationStyleOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_MEDITATION_STYLE, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: meditationTeacherOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: meditationDurationOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_MEDITATION_DURATION, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: yogaDurationOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_YOGA_DURATION, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: yogaFocusOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_YOGA_FOCUS, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: yogaLevelOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_YOGA_LEVEL, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: yogaStyleOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_YOGA_STYLE, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: yogaTeacherOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_YOGA_TEACHER, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: interviewGuestOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const { data: interviewHostOptionsData } = useQuery(CONTENT_REFERENCE_TYPE_OPTIONS_QUERY, {
    variables: {
      limit: 1000,
      ...buildQueryVariables(CONTENT_REFERENCE_TYPE_INTERVIEW_HOST, languageSelection),
    },
    notifyOnNetworkStatusChange: true,
  })

  const displaySnackbar = (message, severity = 'info', persist = false) => {
    const snackbarOptions = {
      message,
      severity,
      duration: persist ? null : SNACKBAR_DEFAULT_DURATION,
    }
    setSnackbar(snackbarOptions)
  }
  const handleSnackbarClose = () => setSnackbar(null)
  const handleRowEditStart = (params, event) => {
    if (_includes(GridRowEditStartReasons, params.reason)) {
      _set(event, 'defaultMuiPrevented', true)
    }
  }
  const handleRowEditStop = (params, event) => {
    if (_includes(GridRowEditStopReasons, params.reason)) {
      _set(event, 'defaultMuiPrevented', true)
    }
  }

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel)
  }
  const processRowUpdate = useCallback(
    async (updateRow, originalRow) => {
      const updateReferences = _sortBy(_get(updateRow, 'contentReferencesFrom'))
      const originalReferences = _sortBy(_get(originalRow, 'contentReferencesFrom'))
      if (_isEqual(updateRow, originalRow)) {
        displaySnackbar('No changes made')
        return originalRow
      }
      // create/delete content references before updating the content fields so that the content
      // references are updated in the response to the content update mutation
      if (!_isEqual(updateReferences, originalReferences)) {
        const uniqueUpdateReferences = _difference(updateReferences, originalReferences)
        const uniqueOriginalReferences = _difference(originalReferences, updateReferences)
        const titleGroupIdsToSkip = _map(
          _intersectionWith(
            uniqueUpdateReferences,
            uniqueOriginalReferences,
            (toCreate, toDelete) =>
              _isEqual(_get(toCreate, ['contentTo', 0]), _get(toDelete, ['contentTo', 0])),
          ),
          (r) => _get(r, ['contentTo', 0, 'titleGroupId']),
        )
        // delete references that are no longer present
        await mapPromise(
          _filter(
            uniqueOriginalReferences,
            (r) => !_includes(titleGroupIdsToSkip, _get(r, ['contentTo', 0, 'titleGroupId'])),
          ),
          async ({ id }) => {
            contentReferenceDelete({ variables: { contentReference: { id } } })
          },
        )
        // create references that are new
        await mapPromise(
          _filter(
            uniqueUpdateReferences,
            (r) => !_includes(titleGroupIdsToSkip, _get(r, ['contentTo', 0, 'titleGroupId'])),
          ),
          async (params) => {
            const { contentReferenceType, contentTo } = params
            const { type } = contentReferenceType
            const { contentId: fromContentContentId, contentType: fromContentContentType } =
              updateRow
            const [toContentContentId, toContentContentType] = _split(
              contentTo[0].titleGroupId,
              '-',
            )
            const contentReference = {
              fromContentContentId,
              fromContentContentType,
              toContentContentId: _parseInt(toContentContentId),
              toContentContentType,
              type,
            }
            contentReferenceCreate({ variables: { contentReference } })
          },
        )
      }
      const cleanedRow = _omit(updateRow, [
        'contentReferencesFrom',
        'contentAssetReferences',
        'contentProvider',
      ])
      const response = await contentUpdate({
        variables: {
          language: 'en', // get English results back
          content: cleanedRow,
          generatePath: true,
        },
      })
      const content = _get(response, 'data.contentUpdate.content', {})
      displaySnackbar('Content updated', 'success')
      return content
    },
    [contentReferenceCreate, contentReferenceDelete, contentUpdate],
  )
  const handleProcessRowUpdateError = useCallback((rowUpdateError) => {
    displaySnackbar(rowUpdateError.message, 'error', true)
  }, [])

  const contentReferenceSingleSelectValueParameters = (type, optionsData) => ({
    valueGetter: ({ row }) =>
      _get(
        _filter(_get(row, 'contentReferencesFrom', []), { contentReferenceType: { type } }),
        [0, 'contentTo', 0],
        '',
      ),
    valueSetter: ({ row, value }) => {
      const { contentReferencesFrom: existingContentReferencesFrom = [] } = row
      const existingSameTypeContentReferences = _filter(existingContentReferencesFrom, {
        contentReferenceType: { type },
      })
      const valueHasChanged = !_isEqual(
        value,
        _get(existingSameTypeContentReferences, [0, 'contentTo', 0]),
      )
      const contentReferencesFrom = valueHasChanged
        ? _compact([
            ..._filter(
              existingContentReferencesFrom,
              (i) => !_includes(_map(existingSameTypeContentReferences, 'id'), _get(i, 'id')),
            ),
            _has(value, 'titleGroupId') && _has(value, 'title')
              ? {
                  contentReferenceType: { type },
                  contentTo: [{ titleGroupId: value.titleGroupId }],
                }
              : null,
          ])
        : existingContentReferencesFrom
      return { ...row, contentReferencesFrom }
    },
    valueFormatter: ({ value }) =>
      _isEmpty(value)
        ? ''
        : _get(
            _find(optionsData, { titleGroupId: value?.titleGroupId }),
            'title',
            `${value?.title}`,
          ),
    getOptionLabel: (o) => _get(o, 'title', ''),
    getOptionValue: (o) => _get(o, 'titleGroupId', ''),
    valueOptions:
      _map(optionsData, ({ title, titleGroupId }) => ({
        key: `${type}-${titleGroupId}`,
        title,
        titleGroupId,
      })) || [],
    renderEditCell: (params) => <ContentReferenceSelect {...params} optionsData={optionsData} />,
  })
  // Based on working with a singleSelect column type and the methods discussed here:
  // https://github.com/mui/mui-x/issues/4410
  // This should be replaced with the multipleSelect column type when implemented
  const contentReferenceMultipleSelectValueParameters = (type, optionsData) => ({
    valueGetter: ({ row }) =>
      _sortBy(
        _map(
          _filter(_get(row, 'contentReferencesFrom', []), { contentReferenceType: { type } }),
          (r) => _get(r, ['contentTo', 0]),
        ),
        ['title'],
      ),
    valueSetter: ({ row, value }) => {
      const { contentReferencesFrom: existingContentReferencesFrom = [] } = row
      const existingSameTypeContentReferences = _filter(existingContentReferencesFrom, {
        contentReferenceType: { type },
      })
      const valueHasChanged = !_isEqual(
        value,
        _sortBy(
          _map(existingSameTypeContentReferences, (r) => _get(r, ['contentTo', 0])),
          ['title'],
        ),
      )
      const contentReferencesFrom = valueHasChanged
        ? _compact([
            ..._filter(
              existingContentReferencesFrom,
              (i) => !_includes(_map(existingSameTypeContentReferences, 'id'), _get(i, 'id')),
            ),
            ..._map(value, (v) =>
              _has(v, 'titleGroupId') && _has(v, 'title')
                ? {
                    contentReferenceType: { type },
                    contentTo: [v],
                  }
                : null,
            ),
          ])
        : existingContentReferencesFrom
      return { ...row, contentReferencesFrom }
    },
    valueFormatter: ({ value }) => {
      if (_isEmpty(value)) return ''
      const titleArray = _map(_castArray(value), (v) =>
        _get(_find(optionsData, { titleGroupId: v?.titleGroupId }), 'title', `${v?.title}`),
      )
      return _join(titleArray, ', ')
    },
    getOptionLabel: (o) => _get(o, 'title', ''),
    getOptionValue: (o) => _get(o, 'titleGroupId', ''),
    valueOptions:
      _map(optionsData, ({ title, titleGroupId }) => ({
        key: `${type}-${titleGroupId}`,
        title,
        titleGroupId,
      })) || [],
    renderEditCell: (params) => (
      <ContentReferenceSelect {...params} multiple={true} optionsData={optionsData} />
    ),
  })

  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      !_isUndefined(data?.contentList?.count) ? data.contentList.count : prevRowCountState,
    )
  }, [data?.contentList?.count, setRowCountState])

  useEffect(() => {
    const setPageUrlFromPreferences = (
      columnsOrder,
      filters,
      columnVisibility,
      languageForUrl,
      pinnedColumnsForUrl,
    ) => {
      let path = locationPathname
      const queryString = []
      if (!_isEmpty(columnsOrder) && !_isEqual(COLUMN_DEFAULT_ORDER, columnsOrder)) {
        queryString.push(`columns=${JSON.stringify(columnsOrder)}`)
      }
      if (!_isEmpty(filters)) {
        queryString.push(`filters=${JSON.stringify(filters)}`)
      }
      if (!_isEmpty(columnVisibility) && !_isEqual(columnVisibility, COLUMN_DEFAULT_VISIBILITY)) {
        queryString.push(`visibility=${JSON.stringify(columnVisibility)}`)
      }
      if (!_isEmpty(languageForUrl) && !_isEqual(languageForUrl, DEFAULT_LANGUAGE_SELECTION)) {
        queryString.push(`language=${JSON.stringify(languageForUrl)}`)
      }
      if (!_isEmpty(pinnedColumnsForUrl) && !_isEqual(pinnedColumnsForUrl, COLUMN_DEFAULT_PINS)) {
        queryString.push(`pinned=${JSON.stringify(pinnedColumnsForUrl)}`)
      }
      path += `${queryString.length > 0 ? '?' : ''}${queryString.join('&')}`
      window.history.pushState(null, null, path)
    }

    setPageUrlFromPreferences(
      columnOrder,
      filterModel?.items,
      columnVisibilityModel,
      languageSelection,
      pinnedColumns,
    )
  }, [
    columnOrder,
    filterModel,
    columnVisibilityModel,
    languageSelection,
    locationPathname,
    pinnedColumns,
  ])

  useEffect(() => {
    setLocationPathname(props.location.getIn(['data', 'pathname'], ''))

    try {
      const columnQueryParam = getLocationQuery(props.location).get('columns', undefined)
      if (columnQueryParam) {
        setColumnOrder(JSON.parse(columnQueryParam))
      } else {
        setColumnOrder(COLUMN_DEFAULT_ORDER)
      }
    } catch (columnQueryError) {
      setColumnOrder(COLUMN_DEFAULT_ORDER)
      displaySnackbar('Error parsing url: Column Order Error. Reverting to default.', 'error', true)
    }

    try {
      const filterModelParam = getLocationQuery(props.location).get('filters', undefined)
      if (filterModelParam) {
        setFilterModel({ items: JSON.parse(filterModelParam) })
      }
    } catch (filterQueryError) {
      setFilterModel({ items: [] })
      displaySnackbar('Error parsing url: Filter Error. Reverting to default.', 'error', true)
    }

    try {
      const visibilityParam = getLocationQuery(props.location).get('visibility', undefined)
      if (visibilityParam) {
        setColumnVisibilityModel(JSON.parse(visibilityParam))
      }
    } catch (visibilityQueryError) {
      setColumnVisibilityModel({})
      displaySnackbar('Error parsing url: Visibility Error. Reverting to default.', 'error', true)
    }

    try {
      const pinnedParam = getLocationQuery(props.location).get('pinned', undefined)
      if (pinnedParam) {
        setPinnedColumns(JSON.parse(pinnedParam))
      }
    } catch (pinnedQueryError) {
      setPinnedColumns(COLUMN_DEFAULT_PINS)
      displaySnackbar('Error parsing url: Pinned Error. Reverting to default.', 'error', true)
    }

    try {
      const languageParam = getLocationQuery(props.location).get('language', undefined)
      if (languageParam) {
        const languageVal = JSON.parse(languageParam).map((val) => val.toLowerCase())
        const languageOptions = LANGUAGE_SELECTION_OPTIONS.map((option) => option.value)
        if (languageOptions.find((languageOption) => _isEqual(languageOption, languageVal))) {
          setLanguageSelection(languageVal)
        }
      }
    } catch (languageQueryError) {
      setLanguageSelection(DEFAULT_LANGUAGE_SELECTION)
      displaySnackbar('Error parsing url: Language Error. Reverting to default.', 'error', true)
    }
  }, [props.location])

  const rows = loading ? [] : _defaultTo(data?.contentList?.content, [])
  const columns = useMemo(() => {
    const handleEditClick = (id) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
    }
    const handleSaveClick = (id) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
    }
    const handleCancelClick = (id) => () => {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      })
      displaySnackbar('Edit Canceled. No changes made')
    }

    const originalColumns = [
      {
        field: 'actions',
        type: 'actions',
        width: 120,
        getActions: ({ row }) => {
          const { contentType, id, previewMediaId, featureMediaId } = row
          const provider = _get(row, 'contentProvider.provider', '')
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit
          const isSeries = contentType === CONTENT_TYPE_SERIES
          const isVideo = contentType === CONTENT_TYPE_VIDEO
          const contentLink =
            provider === ContentProviderProviders.YogaInternational
              ? `/yi/content/${id}`
              : `/content/${id}`
          return _compact([
            isInEditMode ? (
              <Tooltip title="Save">
                <GridActionsCellItem
                  icon={<SaveIcon />}
                  label="Save"
                  sx={{ color: 'primary.main' }}
                  onClick={handleSaveClick(id)}
                />
              </Tooltip>
            ) : null,
            isInEditMode ? (
              <Tooltip title="Cancel">
                <GridActionsCellItem
                  icon={<CancelIcon />}
                  label="Cancel"
                  className="textPrimary"
                  onClick={handleCancelClick(id)}
                  color="inherit"
                />
              </Tooltip>
            ) : null,
            !isInEditMode ? (
              <Tooltip title="Edit">
                <GridActionsCellItem
                  icon={<EditIcon />}
                  label="Edit"
                  className="textPrimary"
                  onClick={handleEditClick(id)}
                  color="inherit"
                />
              </Tooltip>
            ) : null,
            <Tooltip title="Details" key="open-detail-page">
              <GridActionsCellItem
                icon={<ExitToAppIcon />}
                label="Open Detail Page"
                className="textPrimary"
                onClick={() => window.open(contentLink, '_blank')}
                color="inherit"
              />
            </Tooltip>,
            !isInEditMode ? (
              <GridActionsCellItem
                icon={<EditIcon />}
                key={`edit-${id}`}
                label="v1 Edit page"
                onClick={() => window.open(contentLink, '_blank')}
                showInMenu
              />
            ) : null,
            !isInEditMode && (isSeries || isVideo) ? (
              <GridActionsCellItem
                icon={<EditIcon />}
                key={`edit-preview-${id}`}
                label={`Preview media${!previewMediaId ? ' not assigned' : ''}`}
                onClick={() => window.open(`/media/${previewMediaId}`, '_blank')}
                showInMenu
                disabled={!previewMediaId}
              />
            ) : null,
            !isInEditMode && isVideo ? (
              <GridActionsCellItem
                icon={<EditIcon />}
                key={`edit-feature-${id}`}
                label={`Feature media${!featureMediaId ? ' not assigned' : ''}`}
                onClick={() => window.open(`/media/${featureMediaId}`, '_blank')}
                showInMenu
                disabled={!featureMediaId}
              />
            ) : null,
          ])
        },
      },
      {
        field: 'contentId',
        headerName: 'Content ID',
        type: 'number',
        width: 85,
        // 'number' defaults to formatting with toLocaleString() which adds commas
        valueFormatter: ({ value }) => value,
        filterOperators: filterOperatorsNumeric,
      },
      {
        field: 'contentType',
        headerName: 'Content Type',
        type: 'singleSelect',
        valueOptions: getAllContentTypes(),
        sortable: false,
        filterOperators: filterOperatorsSingleSelect,
      },
      {
        field: 'title',
        headerName: 'Title/Name',
        filterOperators: filterOperatorsString,
        editable: true,
        filterable: true,
        width: 350,
      },
      {
        field: CONTENT_REFERENCE_TYPE_SERIES,
        headerName: 'Series',
        width: 250,
        type: 'singleSelect',
        ...contentReferenceSingleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_SERIES,
          seriesOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        filterOperators: filterOperatorsSingleSelect,
        sortable: false,
        editable: true,
      },
      {
        field: 'published',
        headerName: 'Published',
        type: 'boolean',
        width: 80,
        sortable: false,
        filterable: filterOperatorsSingleSelect,
        editable: true,
      },
      {
        field: 'publishedAtDate',
        headerName: 'Air Date',
        type: 'dateTime',
        valueGetter: ({ value }) => new Date(value),
        valueSetter: ({ row, value }) => ({ ...row, publishedAtDate: value.toISOString() }),
        valueFormatter: ({ value }) => {
          const formattedDate = value.toLocaleDateString('en-US', {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: 'numeric',
          })
          return formattedDate
        },
        editable: true,
        sortable: true,
        filterable: true,
        filterOperators: filterOperatorsDate,
        width: 150,
      },
      {
        field: 'contentProviderProvider',
        headerName: 'Content Provider',
        width: 180,
        type: 'singleSelect',
        valueGetter: ({ row }) => _get(row, 'contentProvider.provider', ''),
        valueOptions: CONTENT_PROVIDER_OPTIONS,
        filterable: true,
        filterOperators: [
          {
            label: 'is',
            value: 'equals',
            getApplyFilterFn: (filterItem) => {
              if (!filterItem.value) return null
              return (params) =>
                _get(params.row, 'contentProvider.provider', '') === filterItem.value
            },
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
          {
            label: 'is not',
            value: 'notEquals',
            getApplyFilterFn: (filterItem) => {
              if (!filterItem.value) return null
              return (params) =>
                _get(params.row, 'contentProvider.provider', '') !== filterItem.value
            },
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
        editable: false,
        sortable: true,
      },
      {
        field: 'path',
        headerName: 'Path',
        filterOperators: filterOperatorsString,
        editable: false,
        width: 350,
      },
      {
        field: 'seasonNumber',
        headerName: 'Season',
        type: 'number',
        width: 80,
        filterable: false,
        editable: true,
      },
      {
        field: 'episodeNumber',
        headerName: 'Episode',
        type: 'number',
        width: 80,
        filterable: false,
        editable: true,
      },
      {
        field: CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
        headerName: 'Rainbow Category',
        width: 160,
        type: 'singleSelect',
        ...contentReferenceSingleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
          rainbowCategoryOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        filterOperators: filterOperatorsSingleSelect,
        sortable: false,
        editable: true,
      },
      {
        field: CONTENT_REFERENCE_TYPE_CENTERS,
        headerName: 'Centers',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_CENTERS,
          centersOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: 'contentPolicyPolicyId',
        headerName: 'Content Policy ID',
        type: 'number',
        width: 130,
        // 'number' defaults to formatting with toLocaleString() which adds commas
        valueFormatter: ({ value }) => value,
        filterable: true,
        editable: true,
        filterOperators: filterOperatorsNumeric,
        sortable: false,
      },
      {
        field: 'id',
        headerName: 'ID',
        type: 'number',
        // 'number' defaults to formatting with toLocaleString() which adds commas
        valueFormatter: ({ value }) => value,
        filterOperators: filterOperatorsNumeric,
      },
      {
        field: 'titleGroupId',
        headerName: 'Title Group ID',
        width: 130,
        type: 'string',
        filterable: false,
        sortable: true,
      },
      {
        field: 'titleId',
        headerName: 'Title ID',
        width: 130,
        type: 'string',
        filterable: false,
        sortable: true,
      },
      {
        field: 'language',
        headerName: 'Language',
        filterable: false,
        sortable: false,
      },
      {
        field: 'unpublishedAt',
        headerName: 'Unpublished At',
        type: 'dateTime',
        valueGetter: ({ value }) => new Date(value),
        valueSetter: ({ row, value }) => ({ ...row, publishedAtDate: value.toISOString() }),
        sortable: false,
        filterable: false,
        width: 180,
      },
      {
        field: CONTENT_REFERENCE_TYPE_PRODUCT,
        headerName: 'Product',
        width: 250,
        type: 'singleSelect',
        ...contentReferenceSingleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_PRODUCT,
          productOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        filterOperators: filterOperatorsSingleSelect,
        sortable: false,
        editable: true,
      },
      {
        field: 'previewMediaId',
        headerName: 'Preview Media ID',
        type: 'number',
        width: 130,
        // 'number' defaults to formatting with toLocaleString() which adds commas
        valueFormatter: ({ value }) => value,
        filterable: false,
        sortable: false,
      },
      {
        field: 'featureMediaId',
        headerName: 'Feature Media ID',
        type: 'number',
        width: 130,
        // 'number' defaults to formatting with toLocaleString() which adds commas
        valueFormatter: ({ value }) => value,
        filterable: false,
        sortable: false,
      },
      {
        field: 'primaryLanguage',
        headerName: 'Primary Language',
        width: 130,
        filterable: false,
        sortable: false,
      },
      {
        field: 'contentSearchNoIndex',
        headerName: 'Include in Search',
        type: 'boolean',
        width: 130,
        filterable: false,
        sortable: false,
        editable: true,
        valueGetter: ({ value }) => !value,
        valueSetter: ({ row, value }) => ({ ...row, contentSearchNoIndex: !value }),
      },
      {
        field: 'excludeFromRecommendations',
        headerName: 'Include in Recommendations',
        type: 'boolean',
        width: 130,
        filterable: false,
        sortable: false,
        editable: true,
        valueGetter: ({ value }) => !value,
        valueSetter: ({ row, value }) => ({ ...row, excludeFromRecommendations: !value }),
      },
      {
        field: 'filterType',
        headerName: 'Filter Type(s)',
        width: 200,
        // filterOperators: filterOperatorsString,
        filterable: false,
      },
      {
        field: 'bundle',
        headerName: 'Bundle',
        width: 130,
        type: 'singleSelect',
        valueOptions: BUNDLE_LABELS.keySeq().toArray(),
        filterOperators: filterOperatorsSingleSelect,
        sortable: true,
      },
      {
        field: 'displayType',
        headerName: 'Display Type',
        width: 130,
        // filterOperators: filterOperatorsString,
        filterable: false,
      },
      {
        field: 'productType',
        headerName: 'Product Type',
        width: 130,
        // filterOperators: filterOperatorsString,
        filterable: false,
      },
      {
        field: 'weight',
        headerName: 'Weight',
        type: 'number',
        width: 130,
        // filterOperators: filterOperatorsNumeric,
        filterable: false,
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        type: 'dateTime',
        valueGetter: ({ value }) => new Date(value),
        valueSetter: ({ row, value }) => ({ ...row, publishedAtDate: value.toISOString() }),
        sortable: true,
        filterable: false,
        width: 180,
      },
      {
        field: 'description',
        headerName: 'Description',
        type: 'string',
        width: 350,
        filterable: false,
        editable: false,
        sortable: false,
      },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        type: 'dateTime',
        valueGetter: ({ value }) => new Date(value),
        valueSetter: ({ row, value }) => ({ ...row, publishedAtDate: value.toISOString() }),
        sortable: true,
        filterable: false,
        width: 180,
      },
      {
        field: CONTENT_REFERENCE_TYPE_GENRES,
        headerName: 'Genre',
        width: 150,
        type: 'singleSelect',
        ...contentReferenceSingleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_GENRES,
          genreOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        filterOperators: filterOperatorsSingleSelect,
        sortable: false,
        editable: true,
      },
      {
        field: CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
        headerName: 'Third Party Genre',
        width: 160,
        type: 'singleSelect',
        ...contentReferenceSingleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
          thirdPartyGenreOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        filterOperators: filterOperatorsSingleSelect,
        sortable: false,
        editable: true,
      },
      {
        field: CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
        headerName: 'Content Origin',
        width: 250,
        type: 'singleSelect',
        ...contentReferenceSingleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
          contentOriginOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        filterOperators: filterOperatorsSingleSelect,
        sortable: false,
        editable: true,
      },
      {
        field: 'studio',
        headerName: 'Studio',
        width: 130,
        filterable: false,
        editable: true,
        sortable: true,
      },
      {
        field: 'copyright',
        headerName: 'Copyright',
        width: 130,
        filterable: false,
        editable: true,
        sortable: true,
      },
      {
        field: 'instructor',
        headerName: 'Instructor',
        width: 200,
        filterable: false,
        editable: true,
        sortable: true,
      },
      {
        field: 'cast',
        headerName: 'Cast',
        width: 200,
        filterable: false,
        editable: true,
        sortable: true,
      },
      {
        field: CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
        headerName: 'Interview Host',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
          interviewHostOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
        headerName: 'Interview Guest',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
          interviewGuestOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: CONTENT_REFERENCE_TYPE_CATEGORY,
        headerName: 'Category',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_CATEGORY,
          categoryOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
        headerName: 'Marketing Centers',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
          marketingCentersOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS,
        headerName: 'Meditation Focus',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS,
          meditationFocusOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: CONTENT_REFERENCE_TYPE_MEDITATION_STYLE,
        headerName: 'Meditation Style',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_MEDITATION_STYLE,
          meditationStyleOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER,
        headerName: 'Meditation Teacher',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER,
          meditationTeacherOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: CONTENT_REFERENCE_TYPE_MEDITATION_DURATION,
        headerName: 'Meditation Duration',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_MEDITATION_DURATION,
          meditationDurationOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },

      {
        field: CONTENT_REFERENCE_TYPE_YOGA_DURATION,
        headerName: 'Yoga Duration',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_YOGA_DURATION,
          yogaDurationOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: CONTENT_REFERENCE_TYPE_YOGA_FOCUS,
        headerName: 'Yoga Focus',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_YOGA_FOCUS,
          yogaFocusOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: CONTENT_REFERENCE_TYPE_YOGA_LEVEL,
        headerName: 'Yoga Level',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_YOGA_LEVEL,
          yogaLevelOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: CONTENT_REFERENCE_TYPE_YOGA_STYLE,
        headerName: 'Yoga Style',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_YOGA_STYLE,
          yogaStyleOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: CONTENT_REFERENCE_TYPE_YOGA_TEACHER,
        headerName: 'Yoga Teacher',
        width: 350,
        type: 'singleSelect',
        ...contentReferenceMultipleSelectValueParameters(
          CONTENT_REFERENCE_TYPE_YOGA_TEACHER,
          yogaTeacherOptionsData?.contentList?.content || [],
        ),
        filterable: true,
        sortable: false,
        editable: true,
        filterOperators: [
          {
            // Really should be 'containsAnyOf' but that would require this PR:
            // https://github.com/mui/mui-x/pull/11325/files
            value: 'contains',
            InputComponent: GridFilterInputMultipleSingleSelect,
          },
        ],
      },
      {
        field: 'contentAssetReferences',
        headerName: 'PDF Assets',
        width: 300,
        type: 'string',
        valueGetter: ({ row }) => {
          const assetRefs = _get(row, 'contentAssetReferences', [])
          if (_isEmpty(assetRefs)) return ''

          // Filter for PDF assets only
          const pdfAssetRefs = _filter(assetRefs, (ref) => {
            const mimeType = _get(ref, 'asset.mimeType', '')
            return mimeType === 'application/pdf'
          })

          if (_isEmpty(pdfAssetRefs)) return ''

          return _join(
            _map(pdfAssetRefs, (ref) => {
              const fileName = _get(ref, 'asset.originalFileName', 'unnamed')
              return `${fileName}`
            }),
            ', ',
          )
        },
        valueFormatter: ({ value }) => value,
        filterable: false,
        sortable: false,
        editable: false,
      },
    ]
    return createPrioritySortedColumns(columnOrder, originalColumns)
  }, [
    columnOrder,
    rowModesModel,
    seriesOptionsData?.contentList?.content,
    rainbowCategoryOptionsData?.contentList?.content,
    centersOptionsData?.contentList?.content,
    productOptionsData?.contentList?.content,
    genreOptionsData?.contentList?.content,
    thirdPartyGenreOptionsData?.contentList?.content,
    contentOriginOptionsData?.contentList?.content,
    interviewHostOptionsData?.contentList?.content,
    interviewGuestOptionsData?.contentList?.content,
    categoryOptionsData?.contentList?.content,
    marketingCentersOptionsData?.contentList?.content,
    meditationFocusOptionsData?.contentList?.content,
    meditationStyleOptionsData?.contentList?.content,
    meditationTeacherOptionsData?.contentList?.content,
    meditationDurationOptionsData?.contentList?.content,
    yogaDurationOptionsData?.contentList?.content,
    yogaFocusOptionsData?.contentList?.content,
    yogaLevelOptionsData?.contentList?.content,
    yogaStyleOptionsData?.contentList?.content,
    yogaTeacherOptionsData?.contentList?.content,
  ])

  const onColumnOrderChange = (columnOrderChange) => {
    setColumnOrder((prevColumnOrder) => createNewColumnOrder(prevColumnOrder, columnOrderChange))
  }

  const onPinnedColumnsChange = (pinnedColumnsChange) => {
    setPinnedColumns(pinnedColumnsChange)
  }

  const dataGridStyles = {
    '.MuiDataGrid-booleanCell[data-value="false"]': {
      color: 'red',
    },
    '.MuiDataGrid-booleanCell[data-value="true"]': {
      color: '#00bb00',
    },
  }

  return (
    <div style={{ height: 800, width: '100%' }}>
      <DataGridPro
        sx={dataGridStyles}
        rows={rows}
        columns={columns}
        ignoreDiacritics
        loading={loading}
        editMode="row"
        rowCount={rowCountState}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        filterMode="server"
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
        onColumnOrderChange={onColumnOrderChange}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={onPinnedColumnsChange}
        filterModel={filterModel}
        onFilterModelChange={(newFilterModel) => setFilterModel(newFilterModel)}
        pagination
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={(newPaginationModel) => {
          setPaginationModel(newPaginationModel)
        }}
        slots={{ toolbar: CustomToolbar }}
        slotProps={{
          toolbar: {
            languageSelection,
            setLanguageSelection,
            filterModel,
            setFilterModel,
          },
        }}
        sortMode="server"
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
      />
      {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={handleSnackbarClose}
          autoHideDuration={snackbar.duration}
        >
          <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </div>
  )
}

/**
 * Component to create a content list component
 * @param {Object} props The props
 * @param {Map} props.data The data state
 * @param {Map} props.location The location state
 * @param {String} props.storeKey The store key
 */
function ContentListV2 (props) {
  const { location, data } = props
  const auth = useSelector(selectAuth)
  const config = useSelector(selectConfig)
  const server = config.getIn(['data', 'servers', 'graphql'])
  const removeTypenameLink = removeTypenameFromVariables()
  const httpLink = new HttpLink({
    uri: `${server}/graphql`,
    headers: {
      authorization: createAuthorization(auth),
    },
  })
  const client = new ApolloClient({
    link: from([removeTypenameLink, httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            ContentList: {
              ...offsetLimitPagination(),
              read (existing, { args: { offset, limit } }) {
                return existing && existing.slice(offset, offset + limit)
              },
            },
          },
        },
      },
    }),
  })

  useEffect(() => {
    const error = getViewError({
      data,
      storeKey: CONTENT_STORE_KEY,
      options: locationToOptions(location),
    })
    if (error) {
      alert(error)
    }
  }, [data, location])

  return (
    <Container className="content-list-data-table content-list-data-table--list" fluid={true}>
      <ApolloProvider client={client}>
        <ContentListRows location={location} />
      </ApolloProvider>
    </Container>
  )
}

ContentListV2.propTypes = {
  data: ImmutablePropTypes.map.isRequired,
  location: ImmutablePropTypes.map.isRequired,
}

ContentListV2.defaultProps = {
  storeKey: CONTENT_STORE_KEY,
}

export default compose(connectRedux((state) => _pick(state, ['data', 'location'])))(ContentListV2)
